import React, { useState } from "react";
import { View, Text, Image, Pressable, ActivityIndicator } from "react-native";
import {
  ArrowCircleRight2,
  CloseCircle,
  CloseSquare,
  Lock,
} from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import {
  GROUP_DETAILS_TAB_KEYS,
  GroupDetailsTab,
} from "@/src/constants/groups/group-tabs";
import ScTabs from "../ui-kit/tabs";
import { useAuth } from "@/src/contexts/auth.context";
import { usePermissions } from "@/src/hooks/use-permissions";
import ScButton from "../ui-kit/button";
import { useRouter } from "expo-router";
import { GroupDetailData } from "@/src/models/group.model";
import DropDown from "../ui-kit/drop-down";
import GroupHeaderAvatars from "./group-header-avatars";
import { MembershipStatus } from "@/src/constants/groups/group-membership";
import { useGetGroupMemberCount } from "@/src/services/api/groups/groups.hooks";
import ConfirmModal from "../action-modals/confirm-modal";

type GroupDetailHeaderProps = Readonly<{
  group: GroupDetailData;
  activeTab: GroupDetailsTab;
  onTabChange: (tab: GroupDetailsTab) => void;
  status: MembershipStatus;
  isAdmin: boolean;
  isMember: boolean;
  onJoin: () => void;
  onLeave: (membershipId: string, toastKey: string) => void;
  onDeleteGroup: () => void;
  actionPending: boolean;
  membershipId?: string;
}>;

const GroupDetailHeader = ({
  group,
  activeTab,
  onTabChange,
  status,
  isAdmin,
  isMember,
  onJoin,
  onLeave,
  onDeleteGroup,
  actionPending,
  membershipId,
}: GroupDetailHeaderProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canUpdate } = usePermissions();
  const router = useRouter();

  const { data: memberCount } = useGetGroupMemberCount(group.id);
  const [confirmModal, setConfirmModal] = useState<
    "leave" | "cancel_request" | "delete" | null
  >(null);

  return (
    <View className="bg-chalk overflow-hidden rounded-xl md:border md:border-ash-3">
      {/* Cover Image */}
      <View style={{ width: "100%", height: 180, overflow: "hidden" }}>
        <Image
          source={
            group.coverImageUrl
              ? { uri: group.coverImageUrl }
              : require("@/src/assets/images/default-group-cover.png")
          }
          style={{ width: "100%", height: "100%" }}
          resizeMode="cover"
        />
      </View>

      {/* Group identity row */}
      <View className="px-4 pt-3 pb-2 flex-row items-center justify-between">
        <View className="flex-row items-center gap-3 flex-1">
          {/* Avatar */}
          <View className="w-12 h-12 rounded-full bg-purple-600 items-center justify-center">
            <Text className="text-white font-bold text-base">
              {group.avatarLabel ?? ""}
            </Text>
          </View>

          {/* Name + meta */}
          <View className="flex-1">
            <Text
              className="text-onyx font-semibold text-base"
              numberOfLines={1}
            >
              {group.name}
            </Text>
            <View className="flex-row items-center gap-2 mt-0.5 flex-wrap">
              {group.isPrivate && (
                <View className="flex-row items-center gap-1">
                  <Lock size={13} color="#6b7280" variant="Linear" />
                  <Text className="text-slate text-xs">
                    {t("LABEL_PRIVATE_GROUP")}
                  </Text>
                </View>
              )}
              <Text className="text-slate text-xs">•</Text>
              <GroupHeaderAvatars groupId={group.id} />
              <Text className="text-slate text-xs">
                {memberCount} {t("LABEL_TAB_MEMBERS")}
              </Text>
            </View>
          </View>
        </View>

        <View className="flex-row items-center gap-2">
          {!isMember && status != "active" && !isAdmin && (
            <ScButton
              text="BTN_JOIN_GROUP"
              icon={<ArrowCircleRight2 color="white" />}
              onPress={onJoin}
              disabled={actionPending}
              loading={actionPending}
            />
          )}

          {status == "pending" && (
            <View className="flex-row items-center gap-2">
              <View className="flex-row items-center gap-1 px-3 py-1.5 bg-wheat/15 border border-wheat/40 rounded-full">
                {actionPending && <ActivityIndicator />}
                <Text className="text-wheat text-xs font-medium">
                  {t("LABEL_PENDING_APPROVAL")}
                </Text>
              </View>

              <Pressable
                hitSlop={8}
                className="p-1.5 rounded-full hover:bg-ash-3"
                onPress={() => {
                  setConfirmModal("cancel_request");
                }}
              >
                <CloseCircle color="red" />
              </Pressable>
            </View>
          )}

          {status === "active" && (
            <View className="flex-row items-center gap-2">
              <View className={`flex-row items-center gap-1 px-3 py-1.5 border rounded-full ${isAdmin
                ? "bg-primary/10 border-primary/30"
                : "bg-jade/10 border-jade/40"
                }`}>
                {!isAdmin && actionPending && <ActivityIndicator />}
                <Text className={`text-xs font-medium ${isAdmin ? "text-primary" : "text-jade"
                  }`}>
                  {isAdmin ? t("LABEL_ADMIN") : t("LABEL_JOINED")}
                </Text>
              </View>
              {isMember && (
                <View className="flex-row items-center gap-2">
                  {actionPending && <ActivityIndicator />}
                  <DropDown
                    items={[
                      ...(isAdmin && canUpdate("group")
                        ? [
                            {
                              label: t("BTN_EDIT"),
                              onPress: () =>
                                router.push(`/groups/${group.id}/edit`),
                            },
                          ]
                        : []),
                      isAdmin
                        ? {
                            label: t("BTN_DELETE_GROUP"),
                            onPress: () => setConfirmModal("delete"),
                          }
                        : {
                            label: t("BTN_LEAVE_GROUP"),
                            onPress: () => setConfirmModal("leave"),
                          },
                    ]}
                  />
                </View>
              )}
            </View>
          )}
        </View>
      </View>

      <View className="pt-2 justify-center px-4 pb-4">
        <ScTabs
          tabs={GROUP_DETAILS_TAB_KEYS}
          active={activeTab}
          onChange={onTabChange}
        />
      </View>

      <ConfirmModal
        visible={confirmModal !== null}
        title={
          confirmModal === "delete"
            ? "LABEL_DELETE_GROUP"
            : confirmModal === "leave"
              ? "LABEL_LEAVE_GROUP"
              : "LABEL_CANCEL_REQUEST"
        }
        description={
          confirmModal === "delete"
            ? "QUEST_DELETE_GROUP"
            : confirmModal === "leave"
              ? "QUEST_LEAVE_GROUP"
              : "QUEST_CANCEL_REQUEST"
        }
        cancelText="BTN_CANCEL"
        confirmText={confirmModal === "delete" ? "BTN_DELETE_GROUP" : "BTN_YES"}
        loading={confirmModal === "delete" && actionPending}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => {
          if (confirmModal === "delete") {
            onDeleteGroup();
          } else {
            onLeave(
              membershipId as string,
              confirmModal === "leave"
                ? "LABEL_GROUP_LEFT"
                : "LABEL_REQUEST_CANCELLED",
            );
          }
          setConfirmModal(null);
        }}
      />
    </View>
  );
};

export default GroupDetailHeader;
